/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-no-target-blank */
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { border, colors } from 'theme';
import { usePanel } from 'contexts/panelContext';
import { Box, BoxSection } from 'components/_new/Box';
import { Logo } from 'components/_new/Logo';
import { Spinner } from 'components/_new/Spinner';
import { translatedStaticResourceResolver, getBlogUrl } from 'utils/translatedStaticResourceResolver';
import dayjs from 'dayjs';
import { dateFormat, getRelativeCountdown } from 'utils/dateFormat';
import { Divider } from 'components/_new/Divider';
import { TimelineEventItemTypeEnum } from 'types/Timeline';
import { Button } from 'components/_new/Button';
import { Dialog } from 'components/_new/Dialog';
import { IconSvg } from 'components/_new/Icon/IconSvg';
import { Icon } from 'components/_new/Icon';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import TodoItem from './TodoItem';
import { useTimelineSimple } from '../_common/timeline/useTimeline';
import { TimelineEventDetailsDialog } from '../_common/timeline/TimelineEventDetailsDialog';
import { TimelineList } from '../_common/timeline/TimelineList';

const TimelineSection = () => {
	const { t } = useTranslation();

	const { data, refetch, isLoading } = useTimelineSimple();

	const [relativeCountdownNow, setRelativeCountdownNow] = useState(() => Date.now());

	useEffect(() => {
		const interval = setInterval(() => {
			setRelativeCountdownNow(Date.now());
		}, 60000);

		return () => {
			clearInterval(interval);
		};
	}, []);

	const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
	const selectedEvent = selectedEventId
		? data?.find(({ eventId }) => eventId !== null && eventId === selectedEventId) || null
		: null;

	const handleEventClick = (key: string) => {
		if (data) {
			const timelineEvent = data.find((event) => event.key === key)!;
			if (timelineEvent.type === TimelineEventItemTypeEnum.SESSION) {
				//
			} else {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				setSelectedEventId(timelineEvent.eventId);
			}
		}
	};

	const [showCalendarModalOpen, setShowCalendarModalOpen] = useState(false);

	return (
		<>
			<Dialog
				title={t('timeline.title')}
				visible={showCalendarModalOpen}
				onHide={() => {
					setShowCalendarModalOpen(false);
				}}
				size="lg"
			>
				<TimelineList dialogMode />
			</Dialog>

			<TimelineEventDetailsDialog
				data={selectedEvent!}
				onUpdate={() => {
					refetch();
				}}
				visible={Boolean(selectedEventId) && Boolean(selectedEvent)}
				onHide={() => {
					setSelectedEventId(null);
				}}
			/>

			<Box variant="white-bordered">
				<BoxSection header={t('timeline.events')} noPad={isLoading || (data || []).length > 0}>
					{isLoading ? (
						<Spinner center />
					) : (
						<>
							{(data || []).length > 0 && (
								<div className="flex flex-column justify-content-center px-2">
									<div className="w-full">
										{data?.map(({ key, type, summary, dayDate, startAt, endAt, session }) => {
											const isMilestone = type === TimelineEventItemTypeEnum.MILESTONE;
											const now = dayjs(relativeCountdownNow);
											const startsHour = startAt ? dayjs(startAt).format('HH:mm') : null;
											const endsHour = endAt ? dayjs(endAt).format('HH:mm') : null;
											const dateOnly = dateFormat(dayDate, 'date');
											const countdown = !isMilestone
												? getRelativeCountdown({
														start: startAt || dayDate,
														end: endAt,
													})
												: null;
											const isEventPast = endAt
												? now.valueOf() > dayjs(endAt).valueOf()
												: now.valueOf() > dayjs(dayDate).endOf('day').valueOf();
											const shouldHighlightEvent = Boolean(countdown?.highlight && !isEventPast);
											const upcomingLabel = (() => {
												if (isMilestone) {
													const daysUntil = dayjs(dayDate)
														.startOf('day')
														.diff(now.startOf('day'), 'day');
													if (daysUntil === 0) {
														return t('timeline.milestoneToday');
													}
													if (daysUntil > 0) {
														return t('timeline.milestoneInDays', { count: daysUntil });
													}
													return null;
												}
												if (countdown?.type === 'now') {
													return t('timeline.upcomingNow');
												}
												if (countdown?.type === 'soon') {
													return t('timeline.upcomingInMoment');
												}
												if (countdown?.type === 'distance') {
													return t('timeline.upcomingSoon', {
														distance: countdown.distance,
													});
												}
												return null;
											})();
											const externalLink = session?.link;

											return (
												<Fragment key={key}>
													<Box
														className="relative"
														variant={shouldHighlightEvent ? 'white-rose-bordered' : 'clean'}
														noPad
														hoverable
														full
													>
														<Link
															to={externalLink || '#'}
															className="flex flex-column gap-1 justify-content-start w-full p-2"
															onClick={(event) => {
																if (!externalLink) {
																	handleEventClick(key);
																	event.preventDefault();
																}
															}}
														>
															<div className="mb-1">{summary}</div>
															<div className="flex flex-row align-items-center gap-2">
																<IconSvg
																	name="calendar-full"
																	className="icon-container icon-container-sm text-primary"
																/>
																<div className="text-left flex-1 font-bold">
																	{upcomingLabel && (
																		<div className="text-rose">{upcomingLabel}</div>
																	)}
																	<div>{dateOnly || ''}</div>
																</div>
															</div>

															{type !== TimelineEventItemTypeEnum.MILESTONE && (
																<div className="flex flex-row align-items-center gap-2">
																	<IconSvg
																		name="clock"
																		className="icon-container icon-container-sm text-primary"
																	/>
																	<div className="text-left flex-1 font-bold">
																		{startsHour} - {endsHour}
																	</div>
																</div>
															)}
														</Link>
														{shouldHighlightEvent && (
															<div className="box-warning-icon">
																<Icon
																	name="exclamation-circle"
																	className="warning-icon-color"
																/>
															</div>
														)}
													</Box>
													<Divider margin={3} />
												</Fragment>
											);
										})}
									</div>
									<Button
										label={t('timeline.showFullCalendar')}
										variant="primary-text"
										variantSize="sm"
										onClick={() => {
											setShowCalendarModalOpen(true);
										}}
									/>
								</div>
							)}
							{(data || []).length === 0 && (
								<div className="text-muted">{t('timeline.noUpcomingEvents')}</div>
							)}
						</>
					)}
				</BoxSection>
			</Box>
		</>
	);
};

const Tools = () => {
	const { t } = useTranslation();
	const { tProgram } = useProgramTranslation();
	const navigate = useNavigate();
	const { todosData, currentProgram, userPairs, panelScopePath } = usePanel();
	const timelineEnabled = Boolean(currentProgram?.eventsEnabled);

	// TODS TEST DATA ALL TYPES
	// const todosData: TodosResponse = {
	// 	common: [
	// 		{ type: 'application_during_deletion', variables: {} },
	// 		{ type: 'read_guides', variables: {} },
	// 		{ type: 'no_accepted_pair', variables: {} },
	// 		{ type: 'wait_for_mentee', variables: {} },
	// 		{ type: 'li_label_info', variables: {} },
	// 	],
	// 	pair: [
	// 		{
	// 			name: 'Some person',
	// 			items: [
	// 				{ type: 'pair_during_deletion', variables: {} },
	// 				{
	// 					type: 'confirm_entry_meeting',
	// 					variables: {
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'arrange_first_session_date',
	// 					variables: {
	// 						sessionId: 123,
	// 						sessionNumber: 123,
	// 						sessionDate: new Date().toLocaleString(),
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'contract_initial_sign_required',
	// 					variables: {
	// 						pairId: 123,
	// 						name: 'Jan Kowalski',
	// 					},
	// 				},
	// 				{
	// 					type: 'contract_goals_sign_required',
	// 					variables: {
	// 						pairId: 123,
	// 						name: 'Jan Kowalski',
	// 					},
	// 				},
	// 				{
	// 					type: 'final_report_mentee',
	// 					variables: {
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'final_report_mentor',
	// 					variables: {
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'evaluation_survey_required',
	// 					variables: {
	// 						pairId: 123,
	// 						sessionId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'session_reminder',
	// 					variables: {
	// 						sessionId: 123,
	// 						sessionNumber: 123,
	// 						sessionDate: new Date().toLocaleString(),
	// 						pairId: 123,
	// 					},
	// 				},
	// 				{
	// 					type: 'add_session_date',
	// 					variables: {
	// 						sessionId: 123,
	// 						sessionNumber: 123,
	// 						sessionDate: new Date().toLocaleString(),
	// 						pairId: 123,
	// 					},
	// 				},
	// 			],
	// 		},
	// 	],
	// };

	return (
		<div className="flex flex-column gap-4 p-3 h-full" style={{ borderLeft: border(colors.lightGray) }}>
			<Box variant="white-bordered">
				<BoxSection header={tProgram('userPanel.todo.header')}>
					{todosData.common.length === 0 && todosData.pair.length === 0 ? (
						<p>{tProgram('userPanel.todo.noNotification')}</p>
					) : (
						<>
							{todosData.common.length > 0 && (
								<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
									{todosData.common.map(({ key, ...todo }) => (
										<TodoItem {...todo} key={key} />
									))}
								</ul>
							)}

							{todosData.pair.map(({ name, items }) => {
								if (items.length === 0) return null;
								const pairId =
									items.find((item) => item.variables?.pairId)?.variables?.pairId ||
									userPairs?.find(
										(p) =>
											`${p.mentorMembership?.user?.firstName || ''} ${
												p.mentorMembership?.user?.lastName || ''
											}`.trim() === name ||
											`${p.menteeMembership?.user?.firstName || ''} ${
												p.menteeMembership?.user?.lastName || ''
											}`.trim() === name ||
											p.stats?.mentorUserDisplayName === name ||
											p.stats?.menteeUserDisplayName === name
									)?.id;

								return (
									<div key={name} className="flex flex-column gap-1">
										<div>
											{tProgram('userPanel.todo.pairWith')}: <span className="bold">{name}</span>
										</div>
										<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
											{items.map(({ key, ...todo }) => (
												<TodoItem {...todo} key={key} />
											))}
										</ul>
										{pairId && (
											<div className="mt-2">
												<Button
													label={tProgram('userPanel.todo.processRoadmapButton')}
													variant="primary-outlined"
													variantSize="sm"
													className="w-full text-center"
													onClick={() => {
														navigate(`${panelScopePath}/pair/${pairId}/process-roadmap`);
													}}
												/>
											</div>
										)}
									</div>
								);
							})}
						</>
					)}
				</BoxSection>
			</Box>

			{timelineEnabled && <TimelineSection />}

			<div className="mt-auto flex justify-content-between align-items-end w-full relative pt-4 pb-2">
				<div className="relative">
					<p
						style={{
							position: 'absolute',
							top: '-20px',
							fontSize: '8px',
							margin: 0,
							color: colors.darkPurplishBlue,
						}}
					>
						powered by
					</p>
					<a href={getBlogUrl()} target="_blank" rel="noreferrer">
						<Logo size="md" />
					</a>
				</div>
				<div
					className="flex flex-column align-items-end text-color-secondary gap-1"
					style={{ fontSize: '0.6rem' }}
				>
					<a href={translatedStaticResourceResolver('privacy')} target="_blank">
						{t('userPanel.footer.privacyPolicy')}
					</a>
					<a href={translatedStaticResourceResolver('terms')} target="_blank" rel="noreferrer">
						{t('userPanel.footer.termsAndConditions')}
					</a>
				</div>
			</div>
		</div>
	);
};

export default Tools;
